import React, { useState } from "react";
import {
  Box, Button, Grid, IconButton, TextField, Typography, MenuItem,
  Divider, CircularProgress, alpha, Switch,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import { useCreatePlaylistGroupMutation, useGetCompanyQuery } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { NAVY_DARK } from "../../../theme/tokens";
import SignageSection, {
  signageLabelSx, signageInputSx,
} from "../../../Components/ui/SignageSection";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin, selectCurrentUser } from "../../../Store/Auth/AuthSelector";

const PLAYBACK_MODES = [
  { v: "SEQUENTIAL", label: "Sequential", help: "Play each playlist in order, then start over" },
  { v: "LOOP_ALL", label: "Loop All", help: "Continuously loop through every playlist" },
  { v: "RANDOM", label: "Random", help: "Shuffle the order on every cycle" },
];

const PlaylistGroupCreate: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [createGroup, { isLoading }] = useCreatePlaylistGroupMutation();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [playbackMode, setPlaybackMode] = useState("SEQUENTIAL");
  const [isActive, setIsActive] = useState(true);
  const [companyId, setCompanyId] = useState<number | "">("");

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const currentUser = useAppSelector(selectCurrentUser);
  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];

  const isValid =
    name.trim().length > 0 && (!isSuperAdmin || companyId !== "");

  const handleSubmit = async () => {
    if (!isValid) {
      showErrorMessage(
        isSuperAdmin && companyId === ""
          ? "Please pick a company"
          : "Name is required"
      );
      return;
    }
    try {
      const payload: any = {
        name: name.trim(),
        description: description.trim() || null,
        playback_mode: playbackMode,
        is_active: isActive,
      };
      if (isSuperAdmin) payload.company_id = Number(companyId);
      const res = await createGroup(payload).unwrap();
      if (res.status) {
        const created = (res as any).data;
        showMessage("Playlist group created");
        if (created?.id) navigate(`/playlist-groups/edit/${created.id}`);
        else navigate("/playlist-groups/list");
      } else {
        showErrorMessage((res as any).message || "Failed to create");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to create");
    }
  };

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.15)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton onClick={() => navigate("/playlist-groups/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            boxShadow: "0 2px 8px rgba(124,58,237,0.4)",
          }}>
            <CollectionsBookmarkIcon sx={{ fontSize: 16, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={18} fontWeight={800} color="#fff" letterSpacing="-0.02em">
              New Playlist Group
            </Typography>
            <Typography fontSize={11} color="rgba(255,255,255,0.45)">
              Bundle multiple playlists devices can cycle through
            </Typography>
          </Box>
          <Button variant="outlined" size="small"
            onClick={() => navigate("/playlist-groups/list")}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "rgba(255,255,255,0.15)", color: "#94a3b8", px: 2.5,
              "&:hover": { borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.05)", color: "#e2e8f0" },
            }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation size="small"
            disabled={!isValid || isLoading} onClick={handleSubmit}
            startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
              boxShadow: "0 2px 12px rgba(124,58,237,0.4)",
              "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
              "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
            }}>
            {isLoading ? "Saving…" : "Save & Add Playlists"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <SignageSection
              icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
              title="Group Information"
              badge="Required"
              accentColor="#3b82f6"
              subtitle="Name and description"
            >
              <Grid container spacing={2.5}>
                {isSuperAdmin && (
                  <Grid item xs={12}>
                    <Typography sx={signageLabelSx}>Company *</Typography>
                    <TextField
                      select fullWidth size="small"
                      value={companyId === "" ? "" : String(companyId)}
                      onChange={(e) =>
                        setCompanyId(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      placeholder="Pick the tenant that owns this group"
                      sx={signageInputSx}
                    >
                      <MenuItem value="">— Pick a company —</MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {c.companyName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                {!isSuperAdmin && currentUser?.company && (
                  <Grid item xs={12}>
                    <Typography sx={{ ...signageLabelSx, mb: 0.5 }}>Company</Typography>
                    <Typography sx={{ fontSize: 13, color: "#475569" }}>
                      {currentUser.company.name}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Group Name *</Typography>
                  <TextField fullWidth placeholder="e.g. WingHQ Main Screen"
                    value={name} onChange={(e) => setName(e.target.value)}
                    sx={{
                      ...signageInputSx,
                      "& .MuiOutlinedInput-root": {
                        ...signageInputSx["& .MuiOutlinedInput-root"],
                        fontSize: 15, fontWeight: 600,
                      },
                    }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Description</Typography>
                  <TextField fullWidth size="small" multiline rows={3}
                    placeholder="Optional — describe what this group plays and where…"
                    value={description} onChange={(e) => setDescription(e.target.value)}
                    sx={signageInputSx} />
                </Grid>
              </Grid>
            </SignageSection>
          </Grid>

          <Grid item xs={12} lg={4}>
            <SignageSection
              icon={<SettingsIcon sx={{ fontSize: 18, color: "#7c3aed" }} />}
              title="Playback Settings"
              accentColor="#7c3aed"
              subtitle="How the playlists are cycled"
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Playback Mode</Typography>
                  <TextField select fullWidth size="small"
                    value={playbackMode}
                    onChange={(e) => setPlaybackMode(e.target.value)}
                    sx={signageInputSx}>
                    {PLAYBACK_MODES.map(m => (
                      <MenuItem key={m.v} value={m.v}>
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{m.label}</Typography>
                          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{m.help}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    p: 2, borderRadius: 2.5,
                    bgcolor: isActive ? alpha("#10b981", 0.05) : "#f8fafc",
                    border: `1px solid ${isActive ? alpha("#10b981", 0.2) : "#e2e8f0"}`,
                  }}>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY_DARK }}>Active</Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        Inactive groups can't be assigned to devices
                      </Typography>
                    </Box>
                    <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                  </Box>
                </Grid>
              </Grid>
            </SignageSection>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PlaylistGroupCreate;
